export const currencySymbols = {
  USD: "$",
  EUR: "€",
  BTC: "₿",
  USDT: "$",
  USDC: "$",
  ETH: "⟠",
  SOL: "◎",
  XRP: "X",
  TRUMP: "T",
  XAUT: "XAUT ",
  PAXG: "G",
};

export const defaultCurrencyRounding = {
  USD: 2,
  EUR: 2,
  BTC: 8,
  USDT: 2,
  USDC: 2,
  ETH: 6,
  SOL: 6,
  XRP: 3,
  TRUMP: 3,
  PAXG: 6,
  XAUT: 5,
};

// Minimum thresholds for displaying balances (hide dust amounts below these)
export const dustThresholds = {
  BTC: 0.00000001,   // 8 decimal places
  USDT: 0.01,        // 2 decimal places
  USDC: 0.01,        // 2 decimal places
  USD: 0.01,         // 2 decimal places
};

const DEFAULT_DUST_THRESHOLD = 0.0001; // 4 decimal places for other coins

/**
 * Check if a balance is significant (not dust).
 * Returns true if the absolute value meets the minimum threshold for the currency.
 *
 * Thresholds:
 * - BTC: 0.00000001 (8dp)
 * - USDT/USDC/USD: 0.01 (2dp)
 * - Everything else: 0.0001 (4dp)
 *
 * @param {number} value - The balance value
 * @param {string} currency - The currency code (e.g., 'BTC', 'USDT', 'WIF')
 * @returns {boolean} - True if the balance is significant, false if it's dust
 */
export function isSignificantBalance(value, currency) {
  if (value === null || value === undefined) return false;
  const threshold = dustThresholds[currency?.toUpperCase()] || DEFAULT_DUST_THRESHOLD;
  return Math.abs(value) >= threshold;
}

export function calculateDecimalPlaces(value) {
  if (!value || value === 0) return 2;
  
  const absValue = Math.abs(value);
  
  // For large values, always use at least 2 decimal places
  if (absValue >= 1000) return 2;
  if (absValue >= 100) return 2;
  if (absValue >= 10) return 2;
  if (absValue >= 1) return 4;
  
  // For small values, we need to find the first significant digit
  // Convert to string and find position of first non-zero digit after decimal
  const str = absValue.toString();
  const decimalIndex = str.indexOf('.');
  if (decimalIndex === -1) return 2; // No decimal point
  
  // Count zeros after decimal point
  let zerosAfterDecimal = 0;
  for (let i = decimalIndex + 1; i < str.length; i++) {
    if (str[i] === '0') {
      zerosAfterDecimal++;
    } else {
      break;
    }
  }
  
  // We want to show at least 2 significant digits after the first non-zero
  return Math.min(zerosAfterDecimal + 4, 8);
}

export function getCurrencyRounding(currency, store) {
  const upperCurrency = currency?.toUpperCase();

  // First check if we have a hardcoded value
  if (defaultCurrencyRounding[upperCurrency]) {
    return defaultCurrencyRounding[upperCurrency];
  }

  try {
    const price = store?.getters?.["price/getPrice"]?.(
      upperCurrency.toLowerCase()
    );
    if (price) {
      // Calculate decimal places based on price magnitude
      const magnitude = Math.log10(price);
      let decimalPlaces = Math.max(2, Math.floor(magnitude));

      // Ensure decimal places are between 2 and 8
      decimalPlaces = Math.min(Math.max(decimalPlaces, 2), 8);
      return decimalPlaces;
    }
  } catch (error) {
    console.error("Error getting price for currency:", error);
  }

  // Default to 8 decimal places if we can't determine the price
  return 8;
}

export function formatPrice(price, currency) {
  if (price === null || price === undefined || isNaN(price)) {
    return `${currencySymbols[currency] || "$"}0`;
  }

  // Always use value-based precision for prices
  const precision = calculateDecimalPlaces(price);

  const formattedValue = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  }).format(parseFloat(price));

  return `${currencySymbols[currency] || "$"}${formattedValue}`;
}

export function formatCurrency(value, currency, store, options = {}) {
  if (value === null || value === undefined || isNaN(value)) return null;

  const upperCurrency = currency?.toUpperCase();
  const symbol = currencySymbols[upperCurrency] || upperCurrency || "?";
  
  // If isPrice is true, use value-based precision
  const rounding = options.isPrice 
    ? calculateDecimalPlaces(value)
    : getCurrencyRounding(upperCurrency, store);

  // Special handling for hardcoded currencies
  if (upperCurrency === "USD" || upperCurrency === "USDT" || upperCurrency === "USDC") {
    const fractionDigits = options.isPrice ? rounding : 2;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }

  if (upperCurrency === "BTC") {
    return `${symbol}${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 8,
      maximumFractionDigits: 10,
    }).format(value)}`;
  }

  // For non-hardcoded currencies, format as "123 DOGE" instead of "DOGE123"
  if (!currencySymbols[upperCurrency]) {
    const roundedValue = Math.round(value * 10 ** rounding) / 10 ** rounding;
    const formattedAmount = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: rounding,
      minimumFractionDigits: rounding,
      useGrouping: false,
    }).format(roundedValue);
    return `${formattedAmount} ${symbol}`;
  }

  // Default formatting for other hardcoded currencies
  const roundedValue = Math.round(value * 10 ** rounding) / 10 ** rounding;
  const formattedAmount = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: rounding,
    minimumFractionDigits: rounding,
  }).format(roundedValue);

  return `${symbol}${formattedAmount}`;
}

export function formatAmount(amount, currency = null, store) {
  if (amount === null || amount === undefined) {
    return "0";
  }

  const value = parseFloat(amount);
  if (currency) {
    const precision = getCurrencyRounding(currency, store) || 2;
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
    }).format(value);
    return formatted;
  }

  const str = value.toString();
  const [whole, decimal] = str.split(".");
  if (!decimal) return whole;
  return `${whole}.${decimal.slice(0, 8)}`;
}

export function formatCommission(value, currency) {
  if (value === null || value === undefined) {
    return `0 ${currency}`;
  }
  return `${parseFloat(value).toFixed(8)} ${currency}`;
}

export function formatDate(dateString) {
  if (!dateString) {
    return "";
  }
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDatetime(dateString) {
  if (!dateString) {
    return "";
  }
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatDatetimeFromMicro(microTimestamp) {
  if (!microTimestamp) {
    return "";
  }
  return formatDatetime(microTimestamp / 1e6);
}

export function formatDatetimeFromMilli(milliTimestamp) {
  if (!milliTimestamp) return "";

  const timestamp = typeof milliTimestamp === 'string' ? Number(milliTimestamp) : milliTimestamp;
  return formatDatetime(timestamp);
}

export function formatPercent(value) {
  if (value === null || value === undefined) {
    return null;
  }
  return `${parseFloat((parseFloat(value) * 100).toFixed(2)).toString()}%`;
}

export function formatLargeNumber(value, decimals = 2) {
  if (value === null || value === undefined) {
    return "0";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(parseFloat(value));
}

export function formatCryptoAmount(value, decimals = 8) {
  if (value === null || value === undefined) {
    return "0";
  }
  return parseFloat(value).toFixed(decimals);
}

export function getQuotePrecision(symbol, store) {
  if (!symbol) return 2;
  const [base, quote] = symbol.split("-");

  if (base === "XRP" && quote === "USDT") return 4;

  return getCurrencyRounding(quote, store) || 2;
}

export function formatDuration(timestamp) {
  const start = new Date(timestamp);
  const now = new Date();
  const diffInMs = now - start;

  const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));

  let duration = [];
  if (days > 0) duration.push(`${days}d`);
  if (hours > 0) duration.push(`${hours}h`);
  if (minutes > 0) duration.push(`${minutes}m`);

  return duration.join(" ") || "0m";
}

export function consolidateMultipleCurrencies(
  wallet,
  targetCurrency = "USDT",
  store
) {
  if (!wallet) {
    return 0;
  }
  let totalValue = 0;
  for (const [currency, amount] of Object.entries(wallet)) {
    if (
      currency === targetCurrency ||
      (currency.includes("USD") && targetCurrency.includes("USD"))
    ) {
      totalValue += amount;
      continue;
    }
    const price = store.getters["price/getPrice"](currency.toLowerCase());
    if (price) {
      totalValue += amount * price;
    }
  }
  return totalValue;
}

export function toTitleCase(str) {
  return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
}

// Transfer marker color and display configuration
export const transferMarkerConfig = {
  DEPOSIT: {
    color: '#26a69a',
    label: 'Deposit'
  },
  WITHDRAWAL: {
    color: '#ef5350', 
    label: 'Withdrawal'
  },
  PROFIT_SHARE_PAYMENT: {
    color: '#3b82f6',
    label: 'Profit Share'
  },
  INTERNAL: {
    color: '#f59e0b',
    label: 'Internal'
  },
  POSITIVE_ADJUSTMENT: {
    color: '#10b981',
    label: 'Adjustment (+)'
  },
  NEGATIVE_ADJUSTMENT: {
    color: '#f43f5e',
    label: 'Adjustment (-)'
  }
};

export function getMarkerColor(transferType) {
  return transferMarkerConfig[transferType]?.color || transferMarkerConfig.DEPOSIT.color;
}

export function getMarkerLabel(transferType) {
  return transferMarkerConfig[transferType]?.label || 'Transfer';
}
